// Shared building blocks for the Admin sections (layout only; no colour literals).
import { useId } from "react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { fmtErr } from "@/lib/api";
import { Badge, Icon, RuleResult, SystemBadge, cx, dateTimeET, etSyd } from "@/sentinel";

export const SEVERITY_OPTIONS = [
  { value: "info", label: "info (all)" },
  { value: "medium", label: "medium+" },
  { value: "high", label: "high only" },
];
export const ROLE_OPTIONS = ["viewer", "analyst", "owner"];
export const PASSWORD_MIN = 8;

/** Owner rights for the current user. The server enforces owner on every mutating admin endpoint; this only shapes the UI. */
export function useOwner() {
  const { can } = useAuth();
  return can("owner");
}

/** The API's error detail as a sentence ({detail:{code,message}} or a plain string). */
export function errText(e) {
  const d = e?.response?.data?.detail;
  if (d && typeof d === "object" && !Array.isArray(d) && (d.message || d.code)) return d.message || d.code;
  return fmtErr(d) || e?.message || "Request failed.";
}
export const errCode = (e) => {
  const d = e?.response?.data?.detail;
  return d && typeof d === "object" ? d.code : null;
};
/** True when an endpoint is missing on this backend (not deployed yet). */
export const isMissing = (e) => e?.response?.status === 404 || e?.response?.status === 405;
export const toastErr = (e) => toast.error(errText(e));

/** "2026-08-19 10:01:00 ET · 00:01:00 AEST" — ET first, Sydney second. */
export function stamp(v) {
  if (!v) return "—";
  const d = dateTimeET(v);
  if (d === "—") return String(v);
  return `${d.slice(0, 10)} ${etSyd(v)}`;
}
export function usd2(v) {
  if (v == null || v === "") return "—";
  const n = Number(v);
  if (!Number.isFinite(n)) return String(v);
  return (n < 0 ? "−" : "") + "$" + Math.abs(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
export function emailResult(status) {
  const s = String(status || "").toLowerCase();
  if (/fail|error|bounce|reject/.test(s)) return "FAIL";
  if (/sent|deliver|ok|queued|accepted/.test(s)) return "PASS";
  return "NA";
}

/** Shown in place of (or next to) owner-only controls for other roles. */
export function OwnerOnly({ children = "Owner only. You can read this section; changes need the owner role." }) {
  return <span className="sn-help sn-row" style={{ gap: 4 }}><Icon name="Lock" size={12} />{children}</span>;
}

/** A labelled read-out; the testid sits on the value node. */
export function Kv({ label, children, testId, className }) {
  return (
    <div className={cx("sn-stack", className)} style={{ gap: 2, minWidth: 0 }}>
      <span className="sn-micro">{label}</span>
      <span className="sn-num sn-small" style={{ color: "var(--text-primary)", overflowWrap: "anywhere" }} data-testid={testId}>{children}</span>
    </div>
  );
}

/** A quiet sunken note. */
export function Callout({ icon, children, ...rest }) {
  return (
    <div {...rest} className="sn-row sn-small sn-muted" style={{ flexWrap: "nowrap", alignItems: "flex-start", padding: "var(--space-2) var(--space-3)", background: "var(--surface-sunken)", borderLeft: "3px solid var(--border-strong)", borderRadius: "var(--radius-sm)", lineHeight: "18px" }}>
      {icon && <Icon name={icon} size={16} style={{ flex: "none", marginTop: 1, color: "var(--text-tertiary)" }} />}
      <span style={{ minWidth: 0 }}>{children}</span>
    </div>
  );
}

/** The endpoint is not on this server yet (the backend change has not been deployed). */
export function NotDeployed({ what }) {
  return <Callout icon="CircleSlash" data-testid="not-deployed">{what} is not available on this server yet. It appears once the matching backend change is deployed.</Callout>;
}

export function CodeBlock({ as: Tag = "code", style, children, ...rest }) {
  return (
    <Tag {...rest} className="sn-num sn-small" style={{ display: "block", margin: 0, padding: "var(--space-2) var(--space-3)", background: "var(--surface-sunken)", border: "1px solid var(--border-hairline)", borderRadius: "var(--radius-sm)", color: "var(--text-primary)", overflowWrap: "anywhere", overflowX: "auto", ...style }}>
      {children}
    </Tag>
  );
}

/** A titled block inside a panel, separated by a hairline. */
export function SubSection({ title, icon, meta, testId, children }) {
  return (
    <section className="sn-stack" style={{ gap: "var(--space-2)", paddingTop: "var(--space-4)", borderTop: "1px solid var(--border-hairline)" }} data-testid={testId}>
      <div className="sn-row">
        {icon && <Icon name={icon} style={{ color: "var(--text-tertiary)" }} />}
        <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600, color: "var(--text-primary)" }}>{title}</h3>
        {meta && <span style={{ marginLeft: "auto" }}>{meta}</span>}
      </div>
      {children}
    </section>
  );
}

/** Admin tables: sn-table in the compact density, horizontal scroll, sticky first column. */
export function Tbl({ testId, label, children }) {
  return (
    <div style={{ overflowX: "auto", maxWidth: "100%" }}>
      <table className="sn-table" data-testid={testId} aria-label={label}>{children}</table>
    </div>
  );
}

/** A native select styled as a Sentinel field (kept native so it stays scriptable and accessible). */
export function NativeSelect({ label, options, width, className, disabled, ...rest }) {
  const id = useId();
  return (
    <div className={cx("sn-field", className)} style={width ? { width } : undefined}>
      {label && <label className="sn-field-label" htmlFor={id}>{label}</label>}
      <div className="sn-input-wrap" data-disabled={!!disabled}>
        <select id={id} className="sn-input" disabled={disabled} style={{ background: "var(--input-bg)", cursor: disabled ? "default" : "pointer" }} {...rest}>
          {options.map((o) => {
            const v = typeof o === "object" ? o.value : o;
            return <option key={v} value={v}>{typeof o === "object" ? o.label : o}</option>;
          })}
        </select>
      </div>
    </div>
  );
}

export function TextArea({ label, hint, className, style, ...rest }) {
  const id = useId();
  return (
    <div className={cx("sn-field", className)} style={style}>
      {label && <label className="sn-field-label" htmlFor={id}>{label}</label>}
      <div className="sn-input-wrap" style={{ height: "auto", alignItems: "stretch" }}>
        <textarea id={id} className="sn-input sn-num" aria-describedby={hint ? id + "-h" : undefined}
          style={{ height: "auto", padding: "var(--space-2)", resize: "vertical", fontSize: 12, lineHeight: "16px" }} {...rest} />
      </div>
      {hint && <span className="sn-help" id={id + "-h"}>{hint}</span>}
    </div>
  );
}

/** A 4px bar on the sequential ramp. */
export function Meter({ ratio, label }) {
  const pct = Math.max(0, Math.min(1, ratio || 0)) * 100;
  return (
    <span role="meter" aria-label={label} aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(pct)}
      style={{ display: "block", position: "relative", height: 4, background: "var(--border-hairline)", borderRadius: 2 }}>
      <i style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: `${pct.toFixed(1)}%`, background: pct >= 100 ? "var(--viz-seq-5)" : "var(--viz-seq-4)", borderRadius: 2 }} />
    </span>
  );
}

export function LiveTag() {
  return <Badge icon="Pin">LIVE</Badge>;
}

/** False-positive rate. Over 50% over-triggers; 25–50% is elevated. Weight carries it, not colour. */
export function Fpr({ v }) {
  if (v == null) return "—";
  const lvl = v > 50 ? "high" : v > 25 ? "elevated" : null;
  return <span style={{ fontWeight: lvl === "high" ? 600 : undefined }} title={lvl ? `FPR ${lvl}` : undefined}>{v.toFixed(1)}%{lvl === "high" && <span className="sn-faint"> ▲</span>}</span>;
}
export function FprLegend() {
  return <p className="sn-help" style={{ margin: 0 }}>FPR = share of trip days that were not real shocks. ▲ marks FPR above 50% (over-triggering); 25–50% is elevated.</p>;
}

export function JobResult({ result }) {
  if (!result) return <span className="sn-faint">—</span>;
  if (result === "ok") return <RuleResult result="PASS">OK</RuleResult>;
  if (result === "error") return <SystemBadge kind="job-failed" />;
  return <Badge icon="CircleMinus">{result}</Badge>;
}
export function JobAction({ action }) {
  if (!action) return <span className="sn-faint">—</span>;
  if (action === "pinned") return <Badge icon="Pin">pinned</Badge>;
  if (action === "proposed") return <Badge icon="Gavel">proposed</Badge>;
  return <span>{action}</span>;
}

/** Splits a newline- or comma-separated list. */
export const splitList = (text) => String(text || "").split(/[\n,]/).map((s) => s.trim()).filter(Boolean);

/** A section of the Admin grid. */
export function SectionGrid({ children, testId }) {
  return <div className="sn-grid-12" data-testid={testId}>{children}</div>;
}
